import React, { FC, useState } from "react";

import { useTranslation } from "react-i18next";

import { ArrowLeftBig, EditIcon } from "@/assets/icons";
import { SearchField } from "@/components/atoms";
import { handleHighlight } from "@/core/html";
import { CONVERSATION_NEW_ID } from "@/shared/domain/conversations";

import ConversationMessagePreview from "../ConversationMessagePreview/ConversationMessagePreview";
import { StyledConversationMessageList } from "../ConversationMessagePreview/styled";
import { StyledConversationPreviewColumn } from "./styled";

export type ConversationPreviewListProps = {
    conversationId: string;
    newMessageTitle: string;
    newMessageContent: string;
    conversations: MyBrokerApi.Conversation[];
    onMessagePreview: (conversation: MyBrokerApi.Conversation) => void;
    onNewConversation: () => void;
    selectedConversation: MyBrokerApi.Conversation;
    onBackPressed?: () => void;
};

const ConversationPreviewList: FC<ConversationPreviewListProps> = ({
    conversations,
    conversationId,
    newMessageTitle,
    newMessageContent,
    onNewConversation,
    onMessagePreview,
    onBackPressed,
    selectedConversation,
}) => {
    const { t } = useTranslation();
    const [textSearch, setTextSearch] = useState("");

    const filteredConversations = conversations
        ?.map((item) => {
            const extraSearch = [];

            if (item.isReadonly) {
                extraSearch.push(t("Common.Info"));
            }
            if (item.isArchived) {
                extraSearch.push(t("Common.Archive"));
            }
            return {
                ...item,
                extraSearch: extraSearch.join(""),
            };
        })
        .filterBy(textSearch, ["topic", "extraSearch"]);

    const handleSearch = (searchWord) => {
        if (searchWord === textSearch) return;
        setTextSearch(searchWord);
        handleHighlight(searchWord);
    };

    const newConversation = {
        id: CONVERSATION_NEW_ID,
        topic: !newMessageTitle || newMessageTitle === "" ? t("Conversation.NewConversation") : newMessageTitle,
        messages: [
            {
                body: !newMessageContent || newMessageContent === "" ? "&nbsp;" : newMessageContent,
                timestamp: new Date(),
            },
        ],
    };

    return (
        <StyledConversationPreviewColumn>
            {filteredConversations && (
                <>
                    <div className="d-flex align-center" style={{ height: "66px" }}>
                        {onBackPressed && (
                            <div className="conversation-preview-action-icons">
                                <ArrowLeftBig data-testid="icon-button-arrow-left" onClick={onBackPressed} />
                            </div>
                        )}
                        <div className="conversation-title-message">{t("Common.Messages")}</div>
                        <div className="conversation-preview-action-icons">
                            <EditIcon data-testid="icon-button-edit" onClick={onNewConversation} />
                        </div>
                    </div>
                    {conversations.length > 0 && (
                        <>
                            <SearchField placeholder={t("Common.SearchDots")} onSearch={handleSearch} />
                            <StyledConversationMessageList>
                                {conversationId === CONVERSATION_NEW_ID && (
                                    <ConversationMessagePreview
                                        key={`conversation-${conversationId}`}
                                        conversation={newConversation}
                                        selectedConversation={newConversation}
                                    />
                                )}
                                {filteredConversations?.map((conversation) => (
                                    <ConversationMessagePreview
                                        onMessagePreview={onMessagePreview}
                                        key={`conversation-${conversation.id}`}
                                        conversation={conversation}
                                        selectedConversation={selectedConversation}
                                    />
                                ))}
                            </StyledConversationMessageList>
                        </>
                    )}
                    {conversations.length === 0 && (
                        <div className="conversation-empty-message">
                            <span>{t("Conversation.YouHaveNoMessage")}</span>
                        </div>
                    )}
                </>
            )}
        </StyledConversationPreviewColumn>
    );
};

export default ConversationPreviewList;
